import { Box, ChakraProvider } from '@chakra-ui/react'
import { SkipNavLink } from '@chakra-ui/skip-nav'
import { ContentfulLivePreviewProvider } from '@contentful/live-preview/react'
import '@contentful/live-preview/style.css'
import { GoogleTagManager } from '@next/third-parties/google'
import { parse } from 'flatted'
import { type AppProps } from 'next/app'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'

import Footer from '../components/layout/Footer/Footer'
import Navigation from '../components/layout/Navigation/Navigation'
import ErrorBoundary from '../components/shared-components/ErrorBoundary/ErrorBoundary'
import NotificationBar from '../components/shared-components/NotificationBar/NotificationBar'
import PreviewButton from '../components/shared-components/PreviewButton/PreviewButton'
import ProgressBar from '../components/shared-components/ProgressBar/ProgressBar'
import SEO from '../components/shared-components/SEO/SEO'
import { NavigationProvider } from '../context/NavigationProvider'
import '../styles/globals.css'
import { theme } from '../theme'
import { purpleTheme } from '../theme/purpleTheme'
import { CONTENTFUL_ENVIRONMENT } from '../utils/consts'
import { type SlugPageProps } from './[[...slug]]'

const googleTagManagerId = process.env.NEXT_PUBLIC_GTM_ID!

type PageProps = Omit<SlugPageProps, 'page'> & {
  page?: string
}

export default function App({
  Component,
  pageProps,
}: {
  pageProps: PageProps
} & {
  Component: AppProps['Component']
}) {
  const router = useRouter()
  const [headerHeight, setHeaderHeight] = useState<number>(100)
  const [businessFinancePage, setBusinessFinancePage] = useState(false)

  const defaultFocusPosition = useRef<HTMLElement>(null)

  const page = pageProps.page && parse(pageProps.page)

  useEffect(() => {
    router.events.on('routeChangeComplete', () => {
      if (!defaultFocusPosition.current) {
        return
      }

      defaultFocusPosition.current.tabIndex = 0
      defaultFocusPosition.current.focus()
      defaultFocusPosition.current.blur()
      defaultFocusPosition.current.removeAttribute('tabindex')
    })
  })

  useEffect(() => {
    if (!defaultFocusPosition.current) {
      return
    }

    const resizeObserver = new ResizeObserver(() => {
      if (defaultFocusPosition.current) {
        setHeaderHeight(defaultFocusPosition.current.offsetHeight)
      }
    })

    resizeObserver.observe(defaultFocusPosition.current)
    return () => resizeObserver.disconnect()
  }, [])

  const [currentTheme, setCurrentTheme] = useState(theme)

  useEffect(() => {
    if (
      router.asPath.split('/').some(item => item === 'cynergy-business-finance')
    ) {
      setCurrentTheme(purpleTheme)
      setBusinessFinancePage(true)
    } else {
      setCurrentTheme(theme)
      setBusinessFinancePage(false)
    }
  }, [router.asPath])

  const environment = CONTENTFUL_ENVIRONMENT
  const isProduction = environment === 'master'

  return (
    <>
      <ErrorBoundary>
        <ContentfulLivePreviewProvider
          locale="en-GB"
          enableLiveUpdates={!isProduction}
          enableInspectorMode={!isProduction}
        >
          <ChakraProvider theme={currentTheme}>
            <NavigationProvider fullPaths={pageProps.fullPaths}>
              <SEO seo={page} />

              <ProgressBar />

              <PreviewButton />

              <Box id="top">
                <Box
                  as="header"
                  position="fixed"
                  width="100%"
                  top="0"
                  zIndex="sticky"
                  ref={defaultFocusPosition}
                >
                  <SkipNavLink fontFamily="Gotham Rounded" zIndex="skipLink">
                    Skip to content
                  </SkipNavLink>

                  {pageProps.siteWideSettings?.notifications &&
                    pageProps.siteWideSettings.notifications.map(
                      notification => {
                        return (
                          <NotificationBar
                            key={`${notification.sys.id}`}
                            id={`${notification.sys.id}-${notification.sys.updatedAt}`}
                            type={notification.fields.notificationType}
                            content={notification.fields.notificationBody}
                            entryId={notification.sys.id}
                          />
                        )
                      },
                    )}
                  <Navigation
                    navItems={pageProps.navigation?.navItems ?? []}
                    loginText={pageProps.navigation?.loginText ?? ''}
                    loginLinks={pageProps.navigation?.loginLinks ?? []}
                    logo={pageProps.navigation?.logo}
                    logoUrl={pageProps.navigation?.logoUrl ?? '/'}
                    isBusinessFinance={businessFinancePage}
                  />
                </Box>

                <Box
                  as="main"
                  id="chakra-skip-nav"
                  mt={headerHeight + 'px'}
                  transitionTimingFunction="ease-out"
                >
                  <Component {...pageProps} page={page} />
                </Box>

                <footer role="contentinfo">
                  {pageProps.footer?.id && <Footer {...pageProps.footer} />}
                </footer>
              </Box>
            </NavigationProvider>
          </ChakraProvider>
        </ContentfulLivePreviewProvider>
      </ErrorBoundary>

      {googleTagManagerId && <GoogleTagManager gtmId={googleTagManagerId} />}
    </>
  )
}
